import React, { useEffect, useState } from "react";

type ColorKey = "orange" | "blue" | "green" | "purple";

const COLORS: ColorKey[] = ["orange", "blue", "green", "purple"];

const colorClasses: Record<ColorKey, string> = {
  orange: "bg-orange-300",
  blue: "bg-blue-300",
  green: "bg-green-300",
  purple: "bg-purple-300",
};

const generatePattern = (): ColorKey[] =>
  Array.from({ length: 3 }, () =>
    COLORS[Math.floor(Math.random() * COLORS.length)]
  );

const PatternBuilder = () => {
  const [sequence, setSequence] = useState<ColorKey[]>(generatePattern());
  const [userInput, setUserInput] = useState<ColorKey[]>([]);
  const [started, setStarted] = useState(false);
  const [result, setResult] = useState<"win" | "lose" | null>(null);
  const [locked, setLocked] = useState(false);
  const [animatePattern, setAnimatePattern] = useState(false);

  const handleBegin = () => {
    setStarted(true);
    setUserInput([]);
    setResult(null);
    setLocked(false);
  };

  const handleRestart = () => {
    setSequence(generatePattern());
    setUserInput([]);
    setStarted(false);
    setResult(null);
    setLocked(false);
    triggerPatternAnimation();
  };

  const triggerPatternAnimation = () => {
    setAnimatePattern(true);
    setTimeout(() => setAnimatePattern(false), 600);
  };

  useEffect(() => {
    triggerPatternAnimation();
  }, []);

  const handleClick = (color: ColorKey) => {
    if (!started || locked || result !== null) return;

    setUserInput((prev) => {
      const newInput = [...prev, color];
      const index = newInput.length - 1;

      if (sequence[index] !== color) {
        setResult("lose");
        setStarted(false);
        setLocked(true);
        triggerPatternAnimation();
        return newInput;
      }

      if (newInput.length === sequence.length) {
        setResult("win");
        setStarted(false);
        setLocked(true);
        triggerPatternAnimation();
      }

      return newInput;
    });
  };

  const borderColor =
    result === "win"
      ? "border-green-500"
      : result === "lose"
      ? "border-orange-500"
      : "border-transparent";

  return (
    <div className="flex flex-col items-center">

      {/* Pattern Preview */}
      {!started && (
        <div
          className={`flex gap-4 mb-6 p-4 rounded-2xl border-4 transition-all duration-500 ${borderColor}`}
        >
          {sequence.map((color, index) => (
            <div
              key={index}
              className={`
                w-14 h-14 rounded-xl transition-all duration-500
                ${colorClasses[color]}
                ${animatePattern ? "scale-110 opacity-100" : "scale-100 opacity-80"}
              `}
            />
          ))}
        </div>
      )}

      {/* Input Buttons */}
      {started && (
        <div className="grid grid-cols-2 gap-4 mb-6">
          {COLORS.map((color) => (
            <button
              key={color}
              onClick={() => handleClick(color)}
              className={`
                w-16 h-16 rounded-xl
                ${colorClasses[color]}
                hover:scale-105 active:scale-95
                transition-transform duration-150
              `}
            />
          ))}
        </div>
      )}

    {/* Messages */}
      {result === "win" && (
        <p className="mb-4 text-green-600 font-medium animate-fadeIn">
          Well done
        </p>
      )}

      {result === "lose" && (
        <p className="mb-4 text-orange-600 font-medium animate-fadeIn">
          Let’s try that again
        </p>
      )}


      {/* Controls */}
      <div className="flex gap-4">
        {!started && result === null && (
          <button
            onClick={handleBegin}
            className="px-5 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition"
          >
            Begin
          </button>
        )}

        {(result === "win" || result === "lose") && (
          <button
            onClick={handleRestart}
            className="px-5 py-2 bg-orange-100 text-orange-700 rounded-lg hover:bg-orange-200 transition"
          >
            Restart
          </button>
        )}
      </div>
    </div>
  );
};

export default PatternBuilder;